import { ArrowRight, Clock, Headphones } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TOPIC_TILES } from "@/data/topicRouter";
import { useLessons, type Lesson } from "@/hooks/useLessons";
import { Button } from "@/components/ui/button";
import { resolveLessonPrice, useLessonsPromoUntil } from "@/lib/lessonsPromo";

const lessonBenefit = (description: string | null) => {
  if (!description) return "Aiškiau pamatysi savo situaciją ir išgirsi praktišką kryptį pirmam žingsniui.";
  const firstSentence = description.match(/^.*?[.!?](?:\s|$)/)?.[0]?.trim() ?? description.trim();
  return firstSentence.length > 180 ? `${firstSentence.slice(0, 177).trim()}…` : firstSentence;
};

const TopicRouter = () => {
  const navigate = useNavigate();
  const { data: lessons } = useLessons();
  const promoUntil = useLessonsPromoUntil();
  const [recommended, setRecommended] = useState<Lesson | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const recommendationRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (recommended && recommendationRef.current) {
      recommendationRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [recommended]);

  const handleTileClick = (id: string, titleMatch: string) => {
    const lesson = lessons?.find(l => 
      l.title.toLowerCase().includes(titleMatch.toLowerCase())
    );
    if (lesson) {
      setSelectedId(id);
      setRecommended(lesson);
    } else {
      navigate('/pamokos');
    }
  };

  const getDotColor = (dot: string) => {
    switch (dot) {
      case "red": return "bg-red";
      case "yellow": return "bg-yellow";
      case "black": return "bg-ink";
      default: return "bg-ink";
    }
  };

  return (
    <section className="bg-cream py-24 relative">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mb-16">
          <span className="text-[11px] tracking-[0.22em] uppercase text-ink/55 font-semibold">— Nuo ko pradėti —</span>
          <h2 className="font-marquee text-3xl md:text-5xl font-bold text-ink mt-3 mb-3">
            Pasirink, kas Tau artimiausia.
          </h2>
          <p className="text-ink/65 text-lg whitespace-pre-line">
            Kiekviena pamoka pradeda{"\n"}
            nuo to, kur esi.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TOPIC_TILES.map((topic) => (
            <button
              key={topic.id}
              onClick={() => handleTileClick(topic.id, topic.lessonTitleMatch)}
              aria-pressed={selectedId === topic.id}
              className={`group relative bg-white p-8 rounded-3xl border text-left transition-all duration-300 hover:border-ink/15 hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)] overflow-hidden ${
                selectedId === topic.id ? "border-terracotta" : "border-ink/5"
              }`}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-3 h-3 rounded-full ${getDotColor(topic.dot)}`} />
              </div>
              
              <h3 className="font-bold text-lg text-ink mb-4 group-hover:text-terracotta transition-colors leading-tight">
                {topic.statement}
              </h3>
              
              <div className="flex items-center text-xs font-bold uppercase tracking-wider text-ink/40 group-hover:text-ink transition-colors mt-auto">
                {topic.lessonLabel} <ArrowRight size={14} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
          ))}
        </div>

        {recommended && (() => {
          const pricing = resolveLessonPrice(recommended, promoUntil);
          const price = pricing.amount.toFixed(2).replace(".", ",");
          const lessonPath = `/pamokos?lesson=${recommended.id}`;
          return (
            <div ref={recommendationRef} className="mt-8 border-t border-ink/15 pt-8 scroll-mt-24" aria-live="polite">
              <p className="text-[11px] tracking-[0.22em] uppercase text-terracotta font-semibold mb-3">Rekomenduojama pamoka</p>
              <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-end">
                <div className="max-w-2xl">
                  <h3 className="font-marquee text-2xl md:text-3xl font-bold text-ink">{recommended.title}</h3>
                  <p className="mt-3 text-ink/70 leading-relaxed">{lessonBenefit(recommended.description)}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-ink/60">
                    <span className="inline-flex items-center gap-1.5"><Clock size={15} />{recommended.duration ?? "Audio pamoka"}</span>
                    <span className="font-bold text-ink">€{price}</span>
                    <span>Neribota prieiga</span>
                  </div>
                </div>
                <div className="flex flex-col gap-3 md:min-w-[300px]">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/auth?mode=signup&redirect=${encodeURIComponent(lessonPath)}`)}
                    className="min-h-12 h-auto whitespace-normal rounded-full border-ink/25 text-ink bg-transparent hover:bg-ink/5"
                  >
                    <Headphones size={17} /> Registruotis ir klausyti ištraukos
                  </Button>
                  <Button
                    onClick={() => navigate(lessonPath)}
                    className="min-h-12 h-auto whitespace-normal rounded-full bg-ink text-cream hover:bg-ink/85"
                  >
                    Įsigyti pamoką už €{price} <ArrowRight size={16} />
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </section>
  );
};

export default TopicRouter;
